import React, { useState, useEffect, useRef } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import symposiumReport from '../data/public_symposium_report.pdf';
import eventFlyer260530 from '../data/event_flyer_260530.pdf';
import kogomiColumnPdf from '../data/kogomi_column.pdf';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

interface Report {
  title: string;
  url: string;
  date: string;
  description?: string;
  linkLabel?: string;
  preview?: string;
}

const reports: Report[] = [
  {
    title: 'こごみコラム',
    url: kogomiColumnPdf,
    date: '20260608',
    description: '著者：広報委員 石川美咲',
    linkLabel: 'PDFをダウンロード',
    preview: kogomiColumnPdf,
  },
  {
    title: '【告知】第５回講演会・総会が開催されます。',
    url: eventFlyer260530,
    date: '20260505',
    description: '日時：2026年5月30日（土）13:00〜16:30\n場所：難波市民学習センター（難波OCATの4階）第4研修室\n参加費：無料です。どなたでもご参加下さい。',
    linkLabel: '詳細資料はこちら',
  },
  {
    title: '市民公開シンポジウム「中近世における城郭植生および薬種植物の生産と流通」が開催されました！',
    url: symposiumReport,
    date: '20251206',
  },
];

const formatDate = (date: string) =>
  `${date.slice(0, 4)}/${date.slice(4, 6)}/${date.slice(6, 8)}`;

const PreviewIframe: React.FC<{ url: string; title: string }> = ({ url }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ratio, setRatio] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    getDocument({ url, isEvalSupported: false }).promise
      .then(pdf => pdf.getPage(1))
      .then(async page => {
        if (cancelled) return;
        const vp = page.getViewport({ scale: 1 });
        setRatio(`${vp.width} / ${vp.height}`);
        const renderVp = page.getViewport({ scale: 2 });
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = renderVp.width;
        canvas.height = renderVp.height;
        const ctx = canvas.getContext('2d');
        if (ctx) await page.render({ canvasContext: ctx, viewport: renderVp }).promise;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url]);

  return (
    <div style={{ width: '100%', aspectRatio: ratio ?? '1 / 1', background: 'white', overflow: 'hidden' }}>
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
};

const ReportList: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const reportsPerPage: number = 3;

  const sortedReports = [...reports].sort(
    (a: Report, b: Report) => b.date.localeCompare(a.date)
  );
  const newestReport = sortedReports[0];
  const indexOfLastReport: number = currentPage * reportsPerPage;
  const indexOfFirstReport: number = indexOfLastReport - reportsPerPage;
  const currentReports: Report[] = sortedReports.slice(indexOfFirstReport, indexOfLastReport);
  const totalPages: number = Math.ceil(reports.length / reportsPerPage);

  return (
    <section className="w-full flex flex-col items-center">
      <div className="w-full max-w-3xl">
        {/* セクションヘッダー */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            ニュースレター
          </h2>
          <p className="text-gray-500 text-base">Newsletter</p>
        </div>

        {/* 最新カード */}
        {newestReport && currentReports.includes(newestReport) && (
          <div className="mb-6">
            <div className="bg-white rounded-2xl border-2 border-[#1a4a2e] shadow-sm overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                  <span className="inline-block bg-[#1a4a2e] text-white text-xs font-bold px-3 py-1 rounded-full w-fit">NEW</span>
                  <span className="text-gray-400 text-xs">{formatDate(newestReport.date)}</span>
                </div>
                <p className="font-bold text-base sm:text-lg text-gray-900 leading-snug mb-3">
                  {newestReport.title}
                </p>
                {newestReport.description && (
                  <p className="text-sm text-gray-600 mb-4 whitespace-pre-line leading-relaxed">
                    {newestReport.description}
                  </p>
                )}
                <a
                  href={newestReport.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1a4a2e] hover:text-[#4a7c59] underline underline-offset-2 transition"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  {newestReport.linkLabel ?? '詳細資料はこちら'}
                </a>
                {newestReport.preview && (
                  <div className="mt-4 bg-white">
                    <PreviewIframe url={newestReport.preview} title={newestReport.title} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* その他のカード */}
        {currentReports.filter(r => r !== newestReport).length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {currentReports.filter(r => r !== newestReport).map((report, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200/80 hover:shadow-md transition-shadow overflow-hidden">
                <div className="p-5">
                  <span className="text-xs text-gray-400 mb-2 block">{formatDate(report.date)}</span>
                  <p className="font-semibold text-sm text-gray-800 leading-snug mb-3">
                    {report.title}
                  </p>
                  {report.description && (
                    <p className="text-xs text-gray-500 mb-3 whitespace-pre-line leading-relaxed">
                      {report.description}
                    </p>
                  )}
                  <a
                    href={report.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#4a7c59] hover:text-[#1a4a2e] transition"
                  >
                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                    {report.linkLabel ?? '資料を見る'}
                  </a>
                  {report.preview && (
                    <div className="mt-3 bg-white">
                      <PreviewIframe url={report.preview} title={report.title} />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ページネーション */}
        {totalPages > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            <button
              className={`px-4 py-2 text-sm rounded-full border transition ${currentPage === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              前へ
            </button>
            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={`px-4 py-2 text-sm rounded-full border transition ${currentPage === i + 1 ? 'bg-[#1a4a2e] text-white border-[#1a4a2e]' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className={`px-4 py-2 text-sm rounded-full border transition ${currentPage === totalPages ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-[#1a4a2e] border-[#1a4a2e]/30 hover:bg-[#f5f0e8]'}`}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              次へ
            </button>
          </div>
        )}


      </div>
    </section>
  );
};

export default ReportList;
